import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { RealWorldConnectionCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ClassroomImplementationStep: React.FC<
  StepComponentProps<RealWorldConnectionCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomImplementation, setShowCustomImplementation] =
    useState(false);

  const implementationOptions = [
    {
      id: "Derse Girişte",
      emoji: "🎯",
      label: t("realWorld.implementation.intro"),
      desc: t("realWorld.implementation.introDesc"),
    },
    {
      id: "Kavramaların somutlaştırılması gerektiğinde",
      emoji: "🔍",
      label: t("realWorld.implementation.concrete"),
      desc: t("realWorld.implementation.concreteDesc"),
    },
    {
      id: "Anlamanın derinleştirilmesi amacıyla sorgulama sırasında",
      emoji: "💭",
      label: t("realWorld.implementation.questioning"),
      desc: t("realWorld.implementation.questioningDesc"),
    },
    {
      id: "Grup çalışması yaptırırken",
      emoji: "👥",
      label: t("realWorld.implementation.group"),
      desc: t("realWorld.implementation.groupDesc"),
    },
    {
      id: "Yansıtma yapmasını istediğimde",
      emoji: "🪞",
      label: t("realWorld.implementation.reflection"),
      desc: t("realWorld.implementation.reflectionDesc"),
    },
    {
      id: "Tartışmanın başlangıcı olarak",
      emoji: "💬",
      label: t("realWorld.implementation.discussion"),
      desc: t("realWorld.implementation.discussionDesc"),
    },
  ];

  const handleImplementationToggle = (implementationId: string) => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    const isSelected = currentImplementations.includes(implementationId);

    if (isSelected) {
      const newImplementations = currentImplementations.filter(
        (id) => id !== implementationId,
      );
      onDataChange({ classroomImplementation: newImplementations.join(", ") });
    } else {
      const newImplementations = [...currentImplementations, implementationId];
      onDataChange({ classroomImplementation: newImplementations.join(", ") });
    }
  };

  const handleCustomImplementationToggle = () => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    const hasCustomImplementation = currentImplementations.some(
      (impl) => !implementationOptions.some((option) => option.id === impl),
    );

    if (hasCustomImplementation || showCustomImplementation) {
      const newImplementations = currentImplementations.filter((impl) =>
        implementationOptions.some((option) => option.id === impl),
      );
      onDataChange({ classroomImplementation: newImplementations.join(", ") });
      setShowCustomImplementation(false);
    } else {
      setShowCustomImplementation(true);
    }
  };

  const handleCustomImplementationChange = (value: string) => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    const filteredImplementations = currentImplementations.filter((impl) =>
      implementationOptions.some((option) => option.id === impl),
    );

    if (value.trim()) {
      const newImplementations = [...filteredImplementations, value.trim()];
      onDataChange({ classroomImplementation: newImplementations.join(", ") });
    } else {
      onDataChange({
        classroomImplementation: filteredImplementations.join(", "),
      });
    }
  };

  const isImplementationSelected = (implementationId: string) => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    return currentImplementations.includes(implementationId);
  };

  const isCustomImplementationSelected = () => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    return (
      showCustomImplementation ||
      currentImplementations.some(
        (impl) => !implementationOptions.some((option) => option.id === impl),
      )
    );
  };

  const getCustomImplementationValue = () => {
    const currentImplementations =
      data.classroomImplementation?.split(", ") || [];
    const customImplementation = currentImplementations.find(
      (impl) => !implementationOptions.some((option) => option.id === impl),
    );
    return customImplementation || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("realWorld.steps.implementation.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("realWorld.steps.implementation.description")}
        </p>
      </div>

      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("realWorld.form.classroomImplementation")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("realWorld.form.classroomImplementationDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {implementationOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isImplementationSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleImplementationToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isImplementationSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isImplementationSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Implementation Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomImplementationSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomImplementationToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isCustomImplementationSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomImplementationSelected() && (
                  <div className="w-2.5 h-2.5 bg-white rounded-sm"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("realWorld.implementation.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("realWorld.implementation.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Implementation Input */}
        {isCustomImplementationSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("realWorld.form.customImplementation")}
            </label>
            <input
              type="text"
              value={getCustomImplementationValue()}
              onChange={(e) => handleCustomImplementationChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("realWorld.form.customImplementationPlaceholder")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
